import React from 'react';
import { RentPlan } from 'lib/rent-plans';
/** styles **/
import s from '../../styles/molecules/rent-plan-modal.module.scss';
// Swiper
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
// Images
import BenefitPWan from 'assets/images/benefit-p-wan.png';
import PWan from 'assets/images/p-wan.png';
import SpeechBaloon from 'assets/images/SpeechBaloon.png';

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="16" height="16" rx="8" fill="#00B66A"/>
    <path d="M11.2069 4.79242C11.5975 4.40199 12.2305 4.40193 12.621 4.79242C13.0114 5.18292 13.0114 5.81598 12.621 6.20648L7.63077 11.1957C7.62733 11.1993 7.6245 11.204 7.621 11.2075C7.40112 11.4272 7.10431 11.5233 6.81729 11.4955C6.74267 11.4884 6.66936 11.4706 6.59756 11.4467C6.57778 11.4401 6.55742 11.4351 6.53799 11.4272C6.50748 11.4149 6.47757 11.4007 6.44815 11.3852C6.39024 11.3547 6.33437 11.3185 6.28213 11.2758C6.25593 11.2545 6.23038 11.2309 6.20596 11.2065L6.20499 11.2055L3.37881 8.37836C2.98832 7.98783 2.9883 7.35481 3.37881 6.9643C3.76934 6.57399 4.40242 6.57388 4.79288 6.9643L6.91299 9.08441L11.2069 4.79242Z" fill="white"/>
  </svg>
);

type Props = {
  isOpen: boolean;
  onClose: () => void;
  plans: RentPlan[];
  initialPlan: RentPlan | null;
  onSelect: (plan: RentPlan) => void;
  onSlideChange?: (plan: RentPlan) => void;
};

export const RentPlanModal: React.FC<Props> = ({ isOpen, onClose, plans, initialPlan, onSelect, onSlideChange }) => {
  if (!isOpen || !initialPlan) return null;

  const initialSlideIndex = plans.findIndex(p => p.id === initialPlan.id) || 0;

  return (
    <div className={s.modalOverlay} onClick={onClose}>
        <div className={s.modalContent} onClick={(e) => e.stopPropagation()}>
          <button className={s.modalCloseButton} onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M6 6L18 18" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <Swiper
            modules={[Navigation]}
            navigation
            spaceBetween={0}
            slidesPerView={1}
            initialSlide={initialSlideIndex}
            className={s.modalSlider}
            onSlideChange={(swiper: SwiperType) => {
              const currentPlan = plans[swiper.activeIndex];
              if (onSlideChange && currentPlan) {
                onSlideChange(currentPlan);
              }
            }}
          >
          {plans.map((plan) => (
            <SwiperSlide key={plan.id}>
              <div className={s.modalInner}>
                {/* タイトル */}
                <div className={s.titleSection}>
                    <h3>{plan.features.title}</h3>
                  </div>
                  
                  {/* プラン賃料 */}
                  <div className={s.planPrice}>
                    <span className={s.label}>{plan.label}</span>
                    <span className={s.text}>　賃料 　</span>
                    <span className={s.price}>{plan.price.replace('万', '')}</span>
                    <span className={s.priceUnit}>万円/月</span>
                  </div>
                  
                  {/* 詳細 */}
                  <div className={s.detailsSection}>
                    <div className={s.benefitWrapper}>
                      <div className={s.benefitTitle}>犬の保育園</div>
                      <div className={s.benefitCount}>
                        <span className={s.max}>最大</span><span className={s.number}>{plan.features.mainBenefit.match(/\d+/)?.[0] || '0'}</span>
                        <span className={s.unit}>回/月</span>
                      </div>
                      <div className={s.benefitAvailable}>利用可能</div>
                    </div>
                  </div>
                  
                  <p className={s.detailsNote}>
                    ※ 保育園を利用されない場合は、シッティングや<br />
                    ホテルに振り替えることもできます。
                  </p>
                  
                  {/* 吹き出し画像 */}
                  <div className={s.benefitImageWrapper}>
                    {plan.label === 'C' ? (
                      <>
                        <img src={SpeechBaloon} alt="上記の利用回数以降も、入居者限定のお得料金でご利用OK!" className={s.benefitImage} />
                        <img src={PWan} alt="P-wan" className={s.benefitImagePlanC} />
                      </>
                    ) : (
                      <img src={BenefitPWan} alt="上記の利用回数以降も、入居者限定のお得料金でご利用OK!" className={s.benefitImage} />
                    )}
                  </div>
                  
                  {/* 追加特典 */}
                  {plan.features.additionalBenefits.length > 0 && (
                    <div className={s.additionalSection}>
                      <h4 className={s.additionalTitle}>さらに</h4>
                      <ul className={s.additionalList}>
                        {plan.features.additionalBenefits.map((benefit, index) => (
                          <li 
                            key={index}
                            className={
                              plan.features.additionalBenefits.length === 2 && index === 0 
                                ? s.withBorder 
                                : ''
                            }
                          >
                            <CheckIcon />
                            <span>{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
              </div>
            </SwiperSlide>
          ))}
          </Swiper>
        </div>
  </div>
  );
};